<template>
  <UIDrawer
    :title
    :is-open
    :z-index
    size="lg"
    flat
    :allow-tray
    @minimize="handleMinimize"
    @close="handleClose"
  >
    <div class="asset-drawer">
      <template
        v-for="tab in tabsList"
        :key="tab.key"
      >
        <div
          v-if="initedTabs[tab.key]"
          v-show="tab.key === activeTab"
          class="asset-drawer__content"
        >
          <div
            v-if="tab.key === TABS.INFO"
            class="asset-drawer-form"
          >
            <AccountForm
              :instance
              @close="handleClose"
            />
          </div>
          <AccountTransactions
            v-else
            :instance
            @close="handleClose"
          />
        </div>
      </template>
      <div class="asset-drawer__panel">
        <UIButtonIcon
          v-for="tab in tabsList"
          :key="tab.key"
          :data-refid="tab.dataRefId"
          :label="tab.label"
          :trailing="tab.icon"
          :variant="tab.key === activeTab ? 'secondary' : 'gray'"
          :fill="tab.key === activeTab ? undefined : 'outlined'"
          :disabled="tab.disabled"
          size="sm"
          @click="handleClickTab(tab.key)"
        />
      </div>
    </div>
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'

import { AssetClass } from '..'
import { ModalClass } from '@types'

import {
  INCOME_ACCOUNT_MODAL_TITLE,
  INCOME_ACCOUNT_MODAL_TITLE_NEW,
  EXPENSE_ACCOUNT_MODAL_TITLE,
  EXPENSE_ACCOUNT_MODAL_TITLE_NEW,
} from './utils/const'

import { UIButtonIcon } from '@ui/buttons'
import { UIDrawer } from '@ui/modals'
import AccountForm from './AccountForm.vue'
import AccountTransactions from './AssetDrawerTransactions.vue'

import {
  ArrowsRightLeftIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'

enum TABS {
  INFO = 'info',
  TRANSACTIONS = 'transactions',
}

type Props = {
  modal: ModalClass<AssetClass>
}

const props = defineProps<Props>()

const activeTab = ref<TABS>(TABS.INFO)
const initedTabs = ref<Record<string, boolean>>({})

const instance = computed(() => props.modal.entity)

const isNewAccount = computed(() => instance.value.isNew)

const newTitle = computed(() =>
  instance.value.isIncomeAccount
    ? INCOME_ACCOUNT_MODAL_TITLE_NEW
    : EXPENSE_ACCOUNT_MODAL_TITLE_NEW,
)
const commonTitle = computed(() =>
  instance.value.isIncomeAccount
    ? INCOME_ACCOUNT_MODAL_TITLE
    : EXPENSE_ACCOUNT_MODAL_TITLE,
)
const title = computed(() =>
  isNewAccount.value ? newTitle.value : commonTitle.value,
)

const isOpen = computed(() => props.modal.isOpened)

const zIndex = computed(() => props.modal.zIndex)
const allowTray = computed(() => props.modal.allowTray)

const tabsList = computed(() => [
  {
    key: TABS.INFO,
    label: 'Basic info',
    icon: InformationCircleIcon,
    dataRefId: 'accountDrawerInfo',
  },
  {
    key: TABS.TRANSACTIONS,
    label: 'Transactions',
    icon: ArrowsRightLeftIcon,
    disabled: isNewAccount.value,
    dataRefId: 'accountDrawerTransactions',
  },
])

const handleClickTab = (key: TABS) => {
  activeTab.value = key
}

const handleMinimize = () => {
  props.modal.hide()
}

const handleClose = () => {
  props.modal.conditionalClose()
}

watchEffect(() => {
  initedTabs.value[activeTab.value] = true
})
</script>
